import { styled } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { IFlightPairingStatus } from "@vmosolutions/oc-interfaces";
import classNames from "classnames";
import React from "react";
// Redux
import { useDispatch, useSelector } from "react-redux";
import { openContextMenu } from "src/redux/actions/ContextMenu.Actions";
import { setSelectedFlightPairingStatus } from "src/redux/actions/FlightPairingStatus/FlightPairingStatus.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
// Status Icons
import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import AirplanemodeInactiveIcon from "@mui/icons-material/AirplanemodeInactive";
import _ from "lodash";

interface IFlightStatusCellProps {
  data: IFlightPairingStatus[];
  id?: string; // window ID used to open context menu.
}

const OCFlightStatusCell = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  // FLIGHT PAIRING STATUS ICON
  "& .flight_pairing_status": {
    width: 16,
    height: 16,
    borderRadius: 3,
    margin: "2px 0px",
    "& svg": {
      margin: 2,
      width: 12,
      height: 12,
      "& path": { fill: "black" },
    },
    "&.ok.unassigned": {
      "& svg path": { fill: theme.palette.background.default },
    },

    // Pairing Status deprecated
    // Flight Pairing Status Colors
    "&.no_pairing": { backgroundColor: theme.custom.pairingStatus.noPairingColor },
    "&.missing_crew": { backgroundColor: theme.custom.pairingStatus.missingCrewColor },
    "&.extra_crew": { backgroundColor: theme.custom.pairingStatus.extraCrewColor },
    "&.extra_pos": { backgroundColor: theme.custom.pairingStatus.extraPosColor },
    "&.missing_ac_info": { backgroundColor: theme.custom.pairingStatus.missingACInfoColor },
    "&.ok": { backgroundColor: theme.custom.pairingStatus.okColor },

    "&:hover, &.highlight": {
      border: `2px solid ${theme.palette.secondary.contrastText}`,
    },
    "&.selected": {
      border: `2px solid ${theme.palette.secondary.main}`,
    },
    "&.selected, &:hover, &.highlight": {
      width: 12,
      height: 12,
      boxSizing: "initial",
      "& svg": {
        margin: 0,
        width: 12,
        height: 12,
      },
    },
  },
}));

export const FlightStatusCell = (props: IFlightStatusCellProps): JSX.Element => {
  const { data, id } = props;
  const dispatch = useDispatch();
  const selectedFlightPairingStatus = useSelector(
    (state: IRootState) => state.flightPairingStatus.selectedFlightStatus
  );

  // highlight hovered flights from timeline.
  const hoveredTimelineFlight = useSelector((state: IRootState) => state.timelineFlights.hoveredFlight);

  // Store selected flight pairing status in redux.
  const handleOnClick = (status: IFlightPairingStatus): void => {
    dispatch(setSelectedFlightPairingStatus(status));
  };

  // Open context menu and show selected flight pairing status summary
  const handleOnContextMenu = (fps: IFlightPairingStatus, id: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    if (fps._id !== selectedFlightPairingStatus?._id || !_.isEqual(fps, selectedFlightPairingStatus)) {
      dispatch(setSelectedFlightPairingStatus(fps));
    }
    dispatch(openContextMenu(id));
  };

  if (!data || data.length === 0 || !id) return null;

  return (
    <OCFlightStatusCell>
      {data.map((fps: IFlightPairingStatus) => {
        const isSelected = selectedFlightPairingStatus?._id === fps._id;
        const isHovered = hoveredTimelineFlight?._id === fps._id;
        const { aircraftRegistration, pairingStatuses, pairingShortCodes } = fps;
        const pairingTooltip =
          pairingShortCodes?.length > 0 ? `Associated Pairings: ${pairingShortCodes?.toString()}` : "No Pairings";

        return (
          <Tooltip key={`fps_${fps._id}`} enterDelay={100} placement="top" title={pairingTooltip}>
            <div
              key={`fps_${fps._id}`}
              className={classNames(
                "flight_pairing_status",
                pairingStatuses?.status?.className,
                isHovered && "highlight",
                isSelected && "selected",
                !aircraftRegistration && "unassigned"
              )}
              onClick={() => handleOnClick(fps)}
              onContextMenu={handleOnContextMenu(fps, id)}
            >
              {aircraftRegistration ? <AirplanemodeActiveIcon /> : <AirplanemodeInactiveIcon />}
            </div>
          </Tooltip>
        );
      })}
    </OCFlightStatusCell>
  );
};
